import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Input } from "../../ui/input";
import { Loader2, ArrowLeft, Upload, Search, MessageSquare, Play, Plus, X, Save, ChevronDown } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "@/components/planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import {
  getProcedureFileUsageForWorkspace,
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry,
} from "@/utils/fileUsageReference";
import { getFY_YEAR } from "@/utils/sharepoint-env";
interface OtherAssetsPrepaidExpenseProps {
  onBack?: () => void;
}

const OTAS_WORKSPACE_PREFIX = "OTAS";
const INFERENCE_QUESTION_ID = "OTAS-PREPEXP-INFERENCE";
const INFERENCE_SECTION_KEY = "execution-substantive:Other Assets";
const INFERENCE_QUESTION = "Document the inference and conclusion from the above procedure performed.";

export default function OtherAssetsPrepaidExpense({ onBack }: OtherAssetsPrepaidExpenseProps) {
  const [excelColumns, setExcelColumns] = useState<Array<{ name: string; value: string }>>([]);
  const [isLoadingExcelColumns, setIsLoadingExcelColumns] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>("");
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const [processingStatus, setProcessingStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  const [fileSearchStatus, setFileSearchStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [foundFiles, setFoundFiles] = useState<string[]>([]);
  const [referenceValue, setReferenceValue] = useState<string>("");
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [reportingDate, setReportingDate] = useState<string>("2025-03-31");
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [amountAsPerRecomputation, setAmountAsPerRecomputation] = useState<number>(0);
  const [amountAsPerFS, setAmountAsPerFS] = useState<number>(0);
  const [activeNoteLineLabel, setActiveNoteLineLabel] = useState<string>("30101, 30134 (AS)");
  const [isLoadingReconBase, setIsLoadingReconBase] = useState(false);
  const [userReconciliations, setUserReconciliations] = useState<Array<{ reason: string; amount: number }>>([]);
  const [isSavingRecon, setIsSavingRecon] = useState(false);
  const [reconSaveStatus, setReconSaveStatus] = useState<"idle" | "saved" | "error">("idle");
  const [prepExpRows, setPrepExpRows] = useState<Array<Record<string, any>>>([]);
  const [isLoadingPrepExpRows, setIsLoadingPrepExpRows] = useState(false);
  const [inputsPanelOpen, setInputsPanelOpen] = useState(false);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestionResponse, setInferenceQuestionResponse] = useState<{
    textResponse?: { html: string; plainText: string } | null;
    documents?: Array<{ id: string; type: string; name: string; content?: string; url?: string }>;
  }>({});
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  const REQUIRED_FIELD_KEYS: Array<{ key: string; label: string; required: boolean }> = [
    { key: "period_start", label: "Period Start", required: true },
    { key: "period_end", label: "Period End", required: true },
    { key: "opening_balance", label: "Opening Balance", required: true },
    { key: "addition", label: "Addition", required: true },
    { key: "closing_balance", label: "Closing Balance", required: true },
    { key: "vendor_name", label: "Vendor Name", required: false },
    { key: "nature_of_expenses", label: "Nature of expenses", required: false },
    { key: "current", label: "Current", required: false },
    { key: "non_current", label: "Non Current", required: false },
  ];

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `OTHER_ASSETS_PREPAID_EXPENSE_${normalizedSection}`;
  };

  useEffect(() => {
    searchForFiles();
    loadFileUsageReference();
    loadReconData();
    loadPrepExpOutputRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadCompany = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch {
        setCompanyName("ABC Private Limited");
      }
    };
    void loadCompany();
  }, []);

  useEffect(() => {
    const loadInferenceSaved = async () => {
      if (!companyName) return;
      try {
        const fileName = `Execution_${OTAS_WORKSPACE_PREFIX}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (result?.success && result?.data?.substantiveProcedures) {
          const saved = result.data.substantiveProcedures[INFERENCE_QUESTION_ID];
          if (saved) {
            localStorage.removeItem(`textbox_${INFERENCE_QUESTION_ID}`);
            let textResponse: { html: string; plainText: string } | null = null;
            if (saved.textResponse) {
              textResponse = {
                html: saved.textResponse.html || saved.textResponse.plainText || "",
                plainText:
                  saved.textResponse.plainText ||
                  (saved.textResponse.html ? String(saved.textResponse.html).replace(/<[^>]*>/g, "") : ""),
              };
              if (textResponse.html) {
                localStorage.setItem(`textbox_${INFERENCE_QUESTION_ID}`, textResponse.html);
              }
            }
            const documents = (saved.documents || []).map((docName: string) => ({
              id: `doc_${docName}`,
              type: "linked" as const,
              name: docName,
            }));
            setInferenceQuestionResponse({ textResponse, documents });
            return;
          }
        }
      } catch {
        // Keep local empty state
      }
      localStorage.removeItem(`textbox_${INFERENCE_QUESTION_ID}`);
      setInferenceQuestionResponse({});
    };
    void loadInferenceSaved();
  }, [companyName]);

  const loadPrepExpOutputRows = async () => {
    setIsLoadingPrepExpRows(true);
    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_OTAS_PrepExp.json");
      if (result?.success && result?.data) {
        const rows = Array.isArray(result.data?.Rows) ? result.data.Rows : [];
        setPrepExpRows(rows);
      } else {
        setPrepExpRows([]);
      }
    } catch {
      setPrepExpRows([]);
    } finally {
      setIsLoadingPrepExpRows(false);
    }
  };

  const parseDdMmYyDate = (value: string): Date => {
    const [dd, mm, yy] = value.split("/");
    return new Date(2000 + Number(yy), Number(mm) - 1, Number(dd));
  };

  const getLatestAmountFromAmountDict = (amountDict: Record<string, any>): number => {
    const dates = Object.keys(amountDict || {});
    if (!dates.length) return 0;
    const latestDate = dates.reduce((latest, current) => {
      try {
        return parseDdMmYyDate(current) > parseDdMmYyDate(latest) ? current : latest;
      } catch {
        return latest;
      }
    }, dates[0]);
    return Number(amountDict[latestDate]) || 0;
  };

  const loadReconBaseNumbers = async () => {
    setIsLoadingReconBase(true);
    try {
      let noteLineIds = new Set<number>([30101, 30134]);
      let noteLineLabel = "30101, 30134 (AS)";
      try {
        const frfResult = await (window as any).sharePointAPI?.getFrf?.();
        const frf = frfResult?.success ? String(frfResult.frf || "").trim() : "";
        if (frf === "Indian Accounting Standards (Ind AS)") {
          noteLineIds = new Set<number>([30063, 30100]);
          noteLineLabel = "30063, 30100 (Ind AS)";
        }
      } catch {
        // Keep AS defaults
      }
      setActiveNoteLineLabel(noteLineLabel);

      let fsTotal = 0;
      if ((window as any).sharePointAPI?.downloadTrialBalanceMapping) {
        const mappingResult = await (window as any).sharePointAPI.downloadTrialBalanceMapping({ includeAmounts: true });
        if (mappingResult?.success && mappingResult?.data) {
          const mappingData = mappingResult.data;
          for (const ledgers of Object.values(mappingData)) {
            if (!Array.isArray(ledgers)) continue;
            for (const item of ledgers as any[]) {
              const noteLineId = Number(item?.note_line_id ?? item?.noteLineId ?? -1);
              if (!noteLineIds.has(noteLineId)) continue;
              const amountDict = item?.amount || {};
              fsTotal += getLatestAmountFromAmountDict(amountDict);
            }
          }
        }
      }
      setAmountAsPerFS(fsTotal);

      let recomputationTotal = 0;
      const prepJsonResult = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_OTAS_PrepExp.json");
      if (prepJsonResult?.success && prepJsonResult?.data) {
        const rows = Array.isArray(prepJsonResult.data?.Rows) ? prepJsonResult.data.Rows : [];
        recomputationTotal = rows.reduce((sum: number, row: any) => sum + (Number(row?.["Recomputed Closing"]) || 0), 0);
      }
      setAmountAsPerRecomputation(recomputationTotal);
    } finally {
      setIsLoadingReconBase(false);
    }
  };

  const loadReconData = async () => {
    try {
      await loadReconBaseNumbers();
      const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_OTAS_PrepExpRecon.json");
      if (result?.success && result?.data) {
        const rows = Array.isArray(result.data?.userReconciliations) ? result.data.userReconciliations : [];
        setUserReconciliations(
          rows.map((r: any) => ({
            reason: String(r?.reason || ""),
            amount: Number(r?.amount) || 0,
          }))
        );
      }
    } catch {
      // If file doesn't exist, keep defaults and create on save.
    }
  };

  const difference = amountAsPerRecomputation - amountAsPerFS;
  const userReconciliationTotal = userReconciliations.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const netDifference = difference - userReconciliationTotal;

  const addUserReconciliation = () => {
    setUserReconciliations((prev) => [...prev, { reason: "", amount: 0 }]);
  };

  const updateUserReconciliation = (index: number, field: "reason" | "amount", value: string) => {
    setUserReconciliations((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: field === "amount" ? Number(value) || 0 : value } : row))
    );
  };

  const removeUserReconciliation = (index: number) => {
    setUserReconciliations((prev) => prev.filter((_, i) => i !== index));
  };

  const saveReconciliation = async () => {
    setIsSavingRecon(true);
    setReconSaveStatus("idle");
    try {
      const data = {
        amountAsPerRecomputation,
        amountAsPerFS,
        difference,
        userReconciliations,
        netDifference,
        noteLineIdsApplied: activeNoteLineLabel,
        lastUpdated: new Date().toISOString(),
      };
      const jsonString = JSON.stringify(data, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }
      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: "Execution_OTAS_PrepExpRecon.json",
        referenceValue: "Execution OTAS Prepaid Expense Reconciliation",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_OTAS_PrepExpRecon"],
      });
      if (!uploadResult?.success) {
        throw new Error(uploadResult?.error || "Failed to save reconciliation JSON");
      }
      setReconSaveStatus("saved");
      setTimeout(() => setReconSaveStatus("idle"), 2500);
    } catch {
      setReconSaveStatus("error");
    } finally {
      setIsSavingRecon(false);
    }
  };

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Other Assets", "Prepaid Expense");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  const searchForFiles = async () => {
    setFileSearchStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const result = await window.sharePointAPI.loadComcFiles("Execution_OTAS_PrepExpLis");
        if (result.success && result.data?.files) {
          const files = result.data.files
            .map((f: any) => f.name)
            .filter((name: string) => name && name.trim().length > 0);
          if (files.length > 0) {
            setFoundFiles(files);
            setFileSearchStatus("found");
          } else {
            setFileSearchStatus("not_found");
          }
        } else {
          setFileSearchStatus("not_found");
        }
      } else {
        setFileSearchStatus("not_found");
      }
    } catch {
      setFileSearchStatus("not_found");
    }
  };

  const uploadFile = async () => {
    setUploadStatus("uploading");
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls,.csv";
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          try {
            const base64Content = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(",")[1];
                resolve(base64);
              };
              reader.onerror = () => reject(new Error("Failed to read file"));
              reader.readAsDataURL(file);
            });

            if (window.sharePointAPI?.uploadFile) {
              const result = await window.sharePointAPI.uploadFile({
                fileContent: base64Content,
                fileName: file.name,
                referenceValue: referenceValue,
                folderName: "client",
                fyYear: getFY_YEAR(),
                sectionList: ["Execution_OTAS_PrepExpLis"],
              });

              if (result.success) {
                setSelectedFile(file.name);
                setFoundFiles([file.name]);
                setFileSearchStatus("found");
                setUploadStatus("completed");
              } else {
                setUploadStatus("error");
              }
            } else {
              setUploadStatus("error");
            }
          } catch {
            setUploadStatus("error");
          }
        }
      };
      input.click();
    } catch {
      setUploadStatus("error");
    }
  };

  const loadExcelColumns = async () => {
    if (!selectedFile) return;
    setIsLoadingExcelColumns(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const result = await window.payroll.loadExcelColumns(selectedFile);
        if (result.ok && result.columns) {
          const columns = result.columns.map((col: string) => ({ name: col, value: col }));
          setExcelColumns(columns);
          const fileName = selectedFile.includes(" (") ? selectedFile.split(" (")[0] : selectedFile;
          await updateProcedureFileUsageForWorkspace("Other Assets", "Prepaid Expense", [{ "Search for Files": fileName }]);
          await loadFileUsageReference();
        }
      }
    } finally {
      setIsLoadingExcelColumns(false);
    }
  };

  const handleMappingChange = (fieldKey: string, columnName: string) => {
    setColumnMappings((prev) => ({ ...prev, [fieldKey]: columnName }));
  };

  const allRequiredFieldsMapped = REQUIRED_FIELD_KEYS
    .filter((f) => f.required)
    .every((f) => columnMappings[f.key] && columnMappings[f.key].length > 0);

  const runPrepaid = async () => {
    if (!selectedFile || !allRequiredFieldsMapped || !reportingDate) return;
    setProcessingStatus("running");

    try {
      const selectedMappings: Record<string, string> = {};
      REQUIRED_FIELD_KEYS.forEach((field) => {
        const selected = columnMappings[field.key];
        if (selected && selected.trim()) {
          selectedMappings[field.key] = selected;
        }
      });

      const fileName = selectedFile.includes(" (") ? selectedFile.split(" (")[0] : selectedFile;
      const entries: FileUsageEntry[] = [{ "Search for Files": fileName }, { "Column Mapping": JSON.stringify(selectedMappings) }];
      await updateProcedureFileUsageForWorkspace("Other Assets", "Prepaid Expense", entries);
      await loadFileUsageReference();

      const result = await window.payroll.run("execute_othass_prepaid_expense", {
        inputFiles: [],
        options: {
          file_name: fileName,
          selected_columns: selectedMappings,
          reporting_date: reportingDate,
          section_list: ["Execution_OTAS_PrepExp"],
        },
      });

      if (result.ok) {
        setProcessingStatus("completed");
        await loadReconBaseNumbers();
        await loadPrepExpOutputRows();
      } else {
        setProcessingStatus("error");
      }
    } catch (e) {
      setProcessingStatus("error");
    }
  };

  const saveInferenceQuestion = useCallback(async () => {
    if (!companyName) {
      setInferenceSaveStatus("error");
      setInferenceSaveError("Company name is required");
      return;
    }
    setInferenceSaveStatus("saving");
    setInferenceSaveError(null);
    try {
      const documentManagers = document.querySelectorAll("[data-document-manager]");
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute("data-item-id");
        if (!itemId) continue;
        const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
        if (autoSaveMethod && typeof autoSaveMethod === "function") {
          autoSaveMethod();
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const phaseFormatted = "Execution";
      const fileName = `${phaseFormatted}_${OTAS_WORKSPACE_PREFIX}_Responses.json`;

      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch {
        /* start fresh */
      }
      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }
      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      const textboxFromLocalStorage = localStorage.getItem(`textbox_${INFERENCE_QUESTION_ID}`);
      const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;
      const sectionToFind = `${phaseFormatted}_${OTAS_WORKSPACE_PREFIX}_${INFERENCE_QUESTION_ID}`;

      let documentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            documentNames = dbResult.data
              .map((file: any) => file.name || "")
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch {
        // keep empty document list on failure
      }

      let textResponse: { html: string; plainText: string } = { html: "", plainText: "" };
      if (hasTextboxContent) {
        textResponse = {
          html: textboxFromLocalStorage as string,
          plainText: (textboxFromLocalStorage as string).replace(/<[^>]*>/g, ""),
        };
      } else if (inferenceQuestionResponse?.textResponse) {
        textResponse = inferenceQuestionResponse.textResponse;
      }

      existingData.substantiveProcedures[INFERENCE_QUESTION_ID] = {
        selectedOption: "",
        textResponse,
        documents: documentNames,
        table: [],
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));
      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `${phaseFormatted} ${OTAS_WORKSPACE_PREFIX} Substantive Responses`,
        folderName: "juggernaut",
        fyYear,
        sectionList: [`${phaseFormatted}_${OTAS_WORKSPACE_PREFIX}_Responses`],
      });
      if (!uploadResult?.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus("saved");
      setTimeout(() => setInferenceSaveStatus("idle"), 3000);
    } catch (e: any) {
      setInferenceSaveStatus("error");
      setInferenceSaveError(e?.message || "Failed to save");
    }
  }, [companyName, inferenceQuestionResponse]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Prepaid Expense - Other Assets</h2>
          <p className="text-gray-400">Search file, map columns, and run prepaid expense processing.</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div
            className="w-full cursor-pointer select-none rounded-lg border border-white/15 bg-white/5 px-4 py-3 hover:bg-white/10 transition-colors"
            onClick={() => setInputsPanelOpen((prev) => !prev)}
            role="button"
            aria-expanded={inputsPanelOpen}
          >
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-3 text-white text-base">
                <Search className="h-5 w-5 text-blue-500" />
                Prepaid Expense Inputs
              </CardTitle>
              <div className="flex items-center gap-2 text-white/80">
                <span className="text-sm">{inputsPanelOpen ? "Collapse" : "Expand"}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${inputsPanelOpen ? "rotate-180" : ""}`} />
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Existing Files"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        {inputsPanelOpen && <CardContent className="space-y-6">
          <div className="flex items-center gap-3">
            {fileSearchStatus === "searching" && <span className="text-blue-400">Searching for files...</span>}
            {fileSearchStatus === "found" && <span className="text-green-400 font-bold">FOUND!</span>}
            {fileSearchStatus === "not_found" && <span className="text-yellow-400">No files found</span>}
          </div>

          {fileSearchStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select File Input</Label>
                <Select value={selectedFile} onValueChange={setSelectedFile}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select file" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {foundFiles.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fileUsageReference.find((e) => e["Search for Files"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find((e) => e["Search for Files"])?.["Search for Files"]}
                  </p>
                )}
              </div>
              {selectedFile && (
                <div className="flex items-center gap-3">
                  <Button onClick={loadExcelColumns} disabled={!selectedFile || isLoadingExcelColumns} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {isLoadingExcelColumns ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...
                      </>
                    ) : (
                      "Load Columns"
                    )}
                  </Button>
                  {excelColumns.length > 0 && <span className="text-green-400 text-sm">{excelColumns.length} columns loaded</span>}
                </div>
              )}
            </div>
          )}

          {fileSearchStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-white">Reference Value (optional)</Label>
                  <Input
                    value={referenceValue}
                    onChange={(e) => setReferenceValue(e.target.value)}
                    placeholder="Enter reference value"
                    className="border-white/10 bg-black/40 text-white"
                  />
                </div>
                <Button onClick={uploadFile} disabled={uploadStatus === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">
                  {uploadStatus === "uploading" ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" /> Upload File
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
          <div className="pt-4 border-t border-white/10 space-y-4">
            <div className="text-white/80 text-sm font-medium">Step 2: Map Columns and Run</div>
          <div className="space-y-2">
            <Label className="text-white">Reporting Date</Label>
            <Input
              type="date"
              value={reportingDate}
              onChange={(e) => setReportingDate(e.target.value)}
              className="border-white/10 bg-black/40 text-white"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {REQUIRED_FIELD_KEYS.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label className="text-white">
                  {field.label} {field.required ? <span className="text-red-400">*</span> : <span className="text-white/50">(optional)</span>}
                </Label>
                <Select
                  value={columnMappings[field.key] || ""}
                  onValueChange={(v) => handleMappingChange(field.key, v)}
                  disabled={excelColumns.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={excelColumns.length === 0 ? "Load columns first" : "Select column"} />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">
                    {excelColumns.map((col) => (
                      <SelectItem key={col.value} value={col.value}>
                        {col.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={runPrepaid}
              disabled={!selectedFile || !allRequiredFieldsMapped || !reportingDate || processingStatus === "running"}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {processingStatus === "running" ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                <><Play className="h-4 w-4 mr-2" /> Run Prepaid Expense</>
              )}
            </Button>
          </div>
          </div>
        </CardContent>}
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Prepaid Expense Reconciliation</CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={loadReconData}
              disabled={isLoadingReconBase}
              className="border-white/20 text-white hover:bg-white/10"
            >
              {isLoadingReconBase ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Execution
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-white/10">
              <thead>
                <tr className="bg-black/60">
                  <th className="border border-white/10 px-4 py-2 text-left text-white font-semibold">Particulars</th>
                  <th className="border border-white/10 px-4 py-2 text-right text-white font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">Amount as per recomputation</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {amountAsPerRecomputation.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white">
                    Amount as per FS (Note line ID {activeNoteLineLabel})
                  </td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {amountAsPerFS.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-semibold">Difference</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {difference.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
                {userReconciliations.map((row, index) => (
                  <tr key={`user-recon-${index}`} className="hover:bg-white/5">
                    <td className="border border-white/10 px-4 py-2 text-white">
                      <Input
                        value={row.reason}
                        onChange={(e) => updateUserReconciliation(index, "reason", e.target.value)}
                        className="border-white/10 bg-black/40 text-white"
                        placeholder="User Reconciliation"
                      />
                    </td>
                    <td className="border border-white/10 px-4 py-2">
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={row.amount || ""}
                          onChange={(e) => updateUserReconciliation(index, "amount", e.target.value)}
                          className="border-white/10 bg-black/40 text-white text-right flex-1"
                          placeholder="Amount"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeUserReconciliation(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="hover:bg-white/5">
                  <td className="border border-white/10 px-4 py-2 text-white font-semibold">Net Difference</td>
                  <td className="border border-white/10 px-4 py-2 text-right text-white font-semibold">
                    {netDifference.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <Button
              onClick={addUserReconciliation}
              size="sm"
              variant="outline"
              className="border-white/10 text-white hover:bg-white/10"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Row
            </Button>
            <div className="flex items-center gap-3">
              <Button
                onClick={saveReconciliation}
                disabled={isSavingRecon}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isSavingRecon ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
              {reconSaveStatus === "saved" && (
                <span className="text-green-400 text-sm">Saved to Execution_OTAS_PrepExpRecon.json</span>
              )}
              {reconSaveStatus === "error" && (
                <span className="text-red-400 text-sm">Save failed</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-white">{INFERENCE_QUESTION}</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === "saved" && <span className="text-sm text-green-400">Saved!</span>}
              {inferenceSaveStatus === "error" && inferenceSaveError && <span className="text-sm text-red-400">Error</span>}
              <Button
                type="button"
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === "saving" || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === "saving" ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <DocumentManager
            sectionKey={INFERENCE_SECTION_KEY}
            groupKey="custom-procedure"
            itemId={INFERENCE_QUESTION_ID}
            itemQuestion={INFERENCE_QUESTION}
            itemTextResponse={inferenceQuestionResponse.textResponse ?? null}
            itemDocuments={inferenceQuestionResponse.documents || []}
            onTextResponseChange={(textResponse) =>
              setInferenceQuestionResponse((prev) => ({ ...prev, textResponse }))
            }
            onDocumentsChange={(documents) =>
              setInferenceQuestionResponse((prev) => ({ ...prev, documents }))
            }
            disabled={false}
          />
          {inferenceSaveError && inferenceSaveStatus === "error" && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-400">
              {inferenceSaveError}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Prepaid Expense Output Table</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingPrepExpRows ? (
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading output rows...
            </div>
          ) : prepExpRows.length === 0 ? (
            <div className="text-white/70 text-sm">No rows found in `Execution_OTAS_PrepExp.json`.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-white/10 text-sm">
                <thead>
                  <tr className="bg-black/60">
                    {Object.keys(prepExpRows[0]).map((key) => (
                      <th key={key} className="border border-white/10 px-3 py-2 text-left text-white font-semibold whitespace-nowrap">
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {prepExpRows.map((row, rowIndex) => (
                    <tr key={`prep-exp-row-${rowIndex}`} className="hover:bg-white/5">
                      {Object.keys(prepExpRows[0]).map((key) => {
                        const value = row[key];
                        const displayValue =
                          typeof value === "number"
                            ? value.toFixed(2)
                            : value === null || value === undefined
                            ? ""
                            : String(value);
                        return (
                          <td key={`${rowIndex}-${key}`} className="border border-white/10 px-3 py-2 text-white whitespace-nowrap">
                            {displayValue}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {processingStatus !== "idle" && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <CardTitle className="text-white">Execution Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {processingStatus === "completed" && (
              <div className="text-green-400 text-sm">Successful</div>
            )}
            {processingStatus === "error" && (
              <div className="text-red-400 text-sm">Failed</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Existing Files")]: "Step 1: Search for Existing Files",
              [getSectionQuestionId("Execution Status")]: "Execution Status",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}


